/**
 * Turns a fetched attachment Blob into an actual file download without ever
 * exposing the raw API URL (or a bearer token) in the DOM: the anchor's
 * `href` is a same-origin `blob:` object URL created client-side, never a
 * third-party or token-bearing URL.
 */

// biome-ignore lint/suspicious/noControlCharactersInRegex: intentional — these are exactly the bytes that are unsafe in a filename (ASCII control range + reserved punctuation), not an accidental match.
const UNSAFE_FILENAME_CHARS = /[\x00-\x1f\x7f<>:"/\\|?*]/g;

/** Strips path segments and characters that are unsafe in a filename across OSes/filesystems. */
export function sanitizeDownloadFilename(rawFilename: string): string {
  const baseName = rawFilename.split(/[/\\]/).pop() ?? "";
  const cleaned = baseName.replace(UNSAFE_FILENAME_CHARS, "_").trim();
  return cleaned.length > 0 ? cleaned.slice(0, 255) : "attachment";
}

export async function triggerBlobDownload(blob: Blob, filename: string): Promise<void> {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = sanitizeDownloadFilename(filename);
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to pick up the blob URL before revoking it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
